import React from "react";
import { useRef } from "react";
import { Formik } from "formik";
import { Button, Form, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { invitationService, authenticationService } from "../../../services";
import { PasswordStrength } from "../../../components";
import { LoadingSpinner } from "../../../components/common/Loading/LoadingSpinner";
import { IInviteAcceptance } from "../../../customTypings/user/InviteAcceptance";
import { usePasswordValidation } from "../../../hooks";
import { InvitationAcceptanceFormField } from "./InvitationAcceptanceFormField";
import { showToastError } from "../../../utils";

interface InvitationAcceptanceFormProps {
    inviteAcceptance: IInviteAcceptance;
}

export const InvitationAcceptanceForm: React.FC<InvitationAcceptanceFormProps> = ({
    inviteAcceptance
}) => {
    const navigate = useNavigate();
    const { validatePassword, passwordComplexity } = usePasswordValidation(inviteAcceptance.email);
    const displayNameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);

    const focusInput = (inputRef: React.RefObject<HTMLInputElement>) => {
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSubmit = (
        values: { displayName: string; password: string; confirmPassword: string },
        {
            setSubmitting,
            setFieldError,
        }: any
    ) => {
        inviteAcceptance.displayName = values.displayName;
        inviteAcceptance.password = values.password;
        inviteAcceptance.confirmPassword = values.confirmPassword;
        
        invitationService.accept(inviteAcceptance).then(
            (response) => {
                setSubmitting(false);
                if (response.status !== "Failure") {
                    authenticationService
                        .login(inviteAcceptance.email, inviteAcceptance.password)
                        .then(
                            (response) => {
                                setSubmitting(false);
                                if (response.status !== "Failure") {
                                    navigate("/", { replace: true });
                                } else {
                                    showToastError(response.message);
                                    focusInput(passwordRef);
                                }
                            },
                            (error) => {
                                setSubmitting(false);
                                showToastError(error.status === 400 ? error.message : error);
                                focusInput(passwordRef);
                            }
                        );
                } else {
                    showToastError(response.message);
                    focusInput(displayNameRef);
                }
            },
            (error) => {
                setSubmitting(false);
                if (error.status === 400) {
                    setFieldError("displayName", error.errors.displayName);
                    setFieldError("password", error.errors.Password);
                    setFieldError("confirmPassword", error.errors.ConfirmPassword);
                    showToastError(error.title);

                    if (error.errors.displayName) {
                        focusInput(displayNameRef);
                    } else if (error.errors.Password) {
                        focusInput(passwordRef);
                    } else if (error.errors.ConfirmPassword) {
                        focusInput(confirmPasswordRef);
                    } else {
                        focusInput(displayNameRef);
                    }
                } else {
                    showToastError(error);
                    focusInput(displayNameRef);
                }
            }
        );
    };

    return (
        <Formik
            initialValues={{
                displayName: "",
                password: "",
                confirmPassword: "",
            }}
            onSubmit={handleSubmit}
        >
            {({
                errors,
                touched,
                isSubmitting,
                handleSubmit,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Stack gap={3}>
                        <div>
                            Email: {inviteAcceptance.email}
                        </div>
                        <InvitationAcceptanceFormField
                            name="displayName"
                            label="Display name"
                            type="text"
                            inputRef={displayNameRef}
                        />
                        <InvitationAcceptanceFormField
                            name="password"
                            label="Password"
                            type="password"
                            validate={validatePassword}
                            inputRef={passwordRef}
                        />
                        {passwordComplexity && (
                            <PasswordStrength
                                passwordComplexityResult={passwordComplexity}
                            />
                        )}
                        <InvitationAcceptanceFormField
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            inputRef={confirmPasswordRef}
                        />
                        <div className="form-group d-grid">
                            <Button
                                variant="primary"
                                disabled={isSubmitting}
                                type="submit"
                            >
                                {isSubmitting ? (
                                    <LoadingSpinner text="Creating user account..." />
                                ) : (
                                    "Create User Account"
                                )}
                            </Button>
                        </div>
                    </Stack>
                </Form>
            )}
        </Formik>
    );
};
